import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, mapTo, tap, timeout } from 'rxjs/operators';
import { config } from '../config';
import { Router } from '@angular/router';
import { Token } from '../model/token.model';
import { UserRoles } from '../model/userRoles';
import { Wait } from '../util/timeout';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private static readonly JWT_TOKEN = 'JWT_TOKEN';
  private static readonly REFRESH_TOKEN = 'REFRESH_TOKEN';
  private static readonly USER_INFO = 'userInformationsOverview';
  private loggedUser: string;

  constructor(private http: HttpClient, private router: Router) {}

  public login(user: { loginId: string, password: string }): Observable<any> {
    return this.http.post<any>(`${config.apiUrl}/auth/login`, user)
      .pipe(tap(tokens => {
        this.doLoginUser(user.loginId, tokens)
      }));
  }

  public logout() {
    return this.http.post<any>(`${config.apiUrl}/auth/logout`, {
      'refreshToken': this.getRefreshToken(),
    }).pipe(
      tap(() => this.doLogoutUser()),
      mapTo(true),
      catchError(error => {
        return of(false);
      }));
  }

  isLoggedIn() {
    return !!this.getJwtToken();
  }

  refreshToken(refreshToken) {
    return this.http.post<any>(`${config.apiUrl}/auth/refreshToken`, {
      'refreshToken': refreshToken,
    });
  }

  static getUserIdFromClaims() : number {
    let token = localStorage.getItem(AuthService.JWT_TOKEN);
    if (token) {
      var base64Url = token.split('.')[1];
      var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      var jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
  
      let payloadData = JSON.parse(jsonPayload);
  
      return payloadData.Id;
    }

    return 0;
  }

  static getUserNameFromClaims() : string {
    let token = localStorage.getItem(AuthService.JWT_TOKEN);
    if (token) {
      var base64Url = token.split('.')[1];
      var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      var jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
  
      let payloadData = JSON.parse(jsonPayload);
  
      return payloadData.unique_name;
    }

    return undefined;
  }

  static getUserRoleFromClaims() : string {
    let token = localStorage.getItem(AuthService.JWT_TOKEN);
    if (token) {
      var base64Url = token.split('.')[1];
      var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      var jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
  
      let payloadData = JSON.parse(jsonPayload);
  
      return payloadData.role;
    }

    return UserRoles.none;
  }

  getJwtToken() {
    return localStorage.getItem(AuthService.JWT_TOKEN);
  }

  getRefreshToken() {
    return localStorage.getItem(AuthService.REFRESH_TOKEN);
  }

  private doLoginUser(loginId: string, token: Token) {
    this.loggedUser = loginId;
    this.storeTokens(token);
  }

  public doLogoutUser(): void {
    this.loggedUser = null;
    localStorage.removeItem('habilitedAdvertisement');
    localStorage.removeItem('selectedProfile');
    this.removeTokens();
    this.router.navigate(['/auth/login']);
  }

  storeJwtToken(jwt: string) {
    localStorage.setItem(AuthService.JWT_TOKEN, jwt);
  }

  storeRefreshToken(refreshToken: string){
    localStorage.setItem(AuthService.REFRESH_TOKEN, refreshToken);
  }

  storeTokens(token: Token) {
    localStorage.setItem(AuthService.JWT_TOKEN, token.jwt);
    localStorage.setItem(AuthService.REFRESH_TOKEN, token.refreshToken);
  }

  private removeTokens() {
    localStorage.removeItem(AuthService.JWT_TOKEN);
    localStorage.removeItem(AuthService.REFRESH_TOKEN);
    localStorage.removeItem(AuthService.USER_INFO);
    sessionStorage.clear();
  }

  getIsRegistered(): Observable<any> {
    const url = `${config.apiUrl}/Configuration/IsRegistered`;
    return this.http.get<any>(url)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          console.log(error);      
          throw (error)    
          //return of(error.error);
        }));
  }

  sendRegistrationForm(form: string): Observable<any> {
    return this.http.post<any>(`${config.apiUrl}/Configuration/RegistrationForm`, form)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          throw (error);
        }));
  }

  sendActivationCode(code: string[]): Observable<any> {    
    return this.http.post<any>(`${config.apiUrl}/Configuration/ActivationCode`, code)
    .pipe(timeout(Wait),
      tap( resp => resp),
      catchError(error => {
        throw (error);
      }));
  }

  renewActivationCode(captchaCode: string): Observable<any> {
    return this.http.get<any>(`${config.apiUrl}/Configuration/RenewActivationCode?captchaCode=${captchaCode}`)
      .pipe(timeout(Wait),
        tap(resp => resp),
        catchError(error => {
          throw (error);
        }));
  }

}
